import { FC } from 'react'
import styled from 'styled-components'
import { Text } from 'ui'
import { PersonIcon, HeartIcon, CouponIcon, BellIcon, ShareIcon, WhatsappIcon } from './icons'

export interface PublishBottomBarConfig {
  buttonText: string
  subText: string
  isDisabled: boolean
  showBack: boolean
  backText: string
  isUploading: boolean
  onNext: () => void
  onBack: () => void
}

interface BottomNavMobileProps {
  floatingFilter: 'benefits' | 'liked' | 'personal' | 'notifications'
  onFilterChange: (filter: 'benefits' | 'liked' | 'personal' | 'notifications') => void
  isViewingCoupon?: boolean
  isShareModeActive?: boolean
  isFilterModalOpen?: boolean
  onShareClick?: () => void
  onAcceptClick?: () => void
  onCancelShareMode?: () => void
  checkedCouponsCount?: number
  onWhatsappClick?: () => void
  onApplyFilters?: () => void
  onCancelFilters?: () => void
  filteredCouponsCount?: number
  isShareModalOpen?: boolean
  onConfirmShare?: () => void
  onCloseShareModal?: () => void
  isConfirmShareDisabled?: boolean
  isAcceptCouponModalOpen?: boolean
  isCouponCodeCopied?: boolean
  onContinuePurchase?: () => void
  isPublishCouponModalOpen?: boolean
  publishBottomBarConfig?: PublishBottomBarConfig | null
  isAcceptDisabled?: boolean
}

const BottomNav = styled.div<{ bottomInset?: number; isFilterModalOpen?: boolean; isViewingCoupon?: boolean; isShareModalOpen?: boolean; isAcceptCouponModalOpen?: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #FFFFFF;
  padding-bottom: ${props => props.bottomInset || 0}px;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-around;
  align-items: center;
  border-top: 1px solid #E7E7E7;
  z-index: ${props => (props.isFilterModalOpen || props.isViewingCoupon || props.isShareModalOpen || props.isAcceptCouponModalOpen) ? 10000 : 999};
  border-radius: 14px 14px 0 0;
  height: min-content;
  box-shadow: ${props => (props.isFilterModalOpen || props.isViewingCoupon || props.isShareModalOpen || props.isAcceptCouponModalOpen) ? '0px -4px 12px rgba(0, 0, 0, 0.15)' : 'none'};

  @media (min-width: 1025px) {
    display: none;
  }
`;

const NavItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  cursor: pointer;
`;

const NavIconWrapper = styled.div`
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 12px 16px;
  gap: 12px;
`;

const ShareButton = styled.button`
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid #D1D1D1;
  background: white;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  min-width: 80px;
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1;
  border-radius: 8px;
  border: none;
  background: ${props => props.disabled ? '#D1D1D1' : '#0085FF'};
  color: white;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 16px;
  font-weight: 600;
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`;

const CancelButton = styled.button`
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid #D1D1D1;
  background: white;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  min-width: 80px;
`;

const WhatsappButton = styled.button`
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: #DCFCE4;
  border: none;
  cursor: pointer;
  min-width: 60px;
`;

const BottomNavMobile: FC<BottomNavMobileProps> = ({
  floatingFilter,
  onFilterChange,
  isViewingCoupon,
  isShareModeActive,
  isFilterModalOpen,
  isShareModalOpen,
  onShareClick,
  onAcceptClick,
  onCancelShareMode,
  checkedCouponsCount,
  onWhatsappClick,
  onApplyFilters,
  onCancelFilters,
  filteredCouponsCount,
  onConfirmShare,
  onCloseShareModal,
  isConfirmShareDisabled,
  isAcceptCouponModalOpen,
  isCouponCodeCopied,
  onContinuePurchase,
  isPublishCouponModalOpen,
  publishBottomBarConfig,
  isAcceptDisabled
}) => {
  if (isPublishCouponModalOpen && publishBottomBarConfig) {
    return (
      <BottomNav isViewingCoupon={false} isFilterModalOpen={false} isShareModalOpen={false} isAcceptCouponModalOpen={false} style={{ zIndex: 10000 }}>
        <ActionButtonsContainer>
          {publishBottomBarConfig.showBack && !publishBottomBarConfig.isUploading && (
            <CancelButton onClick={publishBottomBarConfig.onBack}>
              <span>{publishBottomBarConfig.backText}</span>
            </CancelButton>
          )}
          <PrimaryButton onClick={publishBottomBarConfig.onNext} disabled={publishBottomBarConfig.isDisabled}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text size="md" weight="bold" textcolor='white'>{publishBottomBarConfig.buttonText}</Text>
              {publishBottomBarConfig.subText && (
                <span style={{ fontSize: '14px', fontWeight: 300, color: 'white' }}>{publishBottomBarConfig.subText}</span>
              )}
            </div>
          </PrimaryButton>
        </ActionButtonsContainer>
      </BottomNav>
    )
  }

  return (
    <BottomNav isFilterModalOpen={isFilterModalOpen} isViewingCoupon={isViewingCoupon} isShareModalOpen={isShareModalOpen} isAcceptCouponModalOpen={isAcceptCouponModalOpen}>
      {isAcceptCouponModalOpen ? (
        <ActionButtonsContainer>
          <PrimaryButton onClick={onContinuePurchase} disabled={!isCouponCodeCopied}>
            <Text size="md" weight="bold" textcolor='white'>להמשך הרכישה</Text>
          </PrimaryButton>
        </ActionButtonsContainer>
      ) : isFilterModalOpen ? (
        <ActionButtonsContainer>
          <CancelButton onClick={onCancelFilters}>
            <Text size="md" weight="medium" textcolor='#272727'>ביטול</Text>
          </CancelButton>
          <PrimaryButton onClick={onApplyFilters}>
            <Text size="md" weight="bold" textcolor='white'>צפייה בהטבות ({filteredCouponsCount})</Text>
          </PrimaryButton>
        </ActionButtonsContainer>
      ) : isShareModalOpen ? (
        <ActionButtonsContainer>
          <CancelButton onClick={onCloseShareModal}>
            <Text size="md" weight="medium" textcolor='#272727'>ביטול</Text>
          </CancelButton>
          <PrimaryButton onClick={onConfirmShare} disabled={isConfirmShareDisabled}>
            <Text size="md" weight="bold" textcolor='white'>שיתוף {checkedCouponsCount} הטבות עם חברים</Text>
          </PrimaryButton>
        </ActionButtonsContainer>
      ) : isViewingCoupon  ? (
        <ActionButtonsContainer>
          <ShareButton onClick={onShareClick}>
            <ShareIcon fill='#272727' />
            <span>שיתוף</span>
          </ShareButton>
          <PrimaryButton onClick={onAcceptClick} disabled={isAcceptDisabled}>קבלת ההטבה</PrimaryButton>
        </ActionButtonsContainer>
      ) : isShareModeActive ? (
        <ActionButtonsContainer>
          <CancelButton onClick={onCancelShareMode}>
            <Text size="md" weight="medium" textcolor='#272727'>בטל</Text>
          </CancelButton>
          <WhatsappButton onClick={onWhatsappClick}>
            <WhatsappIcon />
          </WhatsappButton>
          <PrimaryButton onClick={onShareClick}>
            <ShareIcon fill='white' />
            <Text size="md" weight="bold" textcolor='white'>שתף {checkedCouponsCount} הטבות</Text>
          </PrimaryButton>
        </ActionButtonsContainer>
      ) : (
        <>
          <NavItem onClick={() => onFilterChange('personal')}>
            <NavIconWrapper>
              <PersonIcon fill={floatingFilter === 'personal' ? '#16AA40' : '#272727'} />
            </NavIconWrapper>
            <Text size='xs' weight='normal' textcolor={floatingFilter === 'personal' ? '#16AA40' : '#272727'}>איזור אישי</Text>
          </NavItem>

          <NavItem onClick={() => onFilterChange('notifications')}>
            <NavIconWrapper>
              <BellIcon fill={floatingFilter === 'notifications' ? '#16AA40' : '#272727'} />
            </NavIconWrapper>
            <Text size='xs' weight='normal' textcolor={floatingFilter === 'notifications' ? '#16AA40' : '#272727'}>התראות</Text>
          </NavItem>

          <NavItem onClick={() => onFilterChange('liked')}>
            <NavIconWrapper>
              <HeartIcon fill={floatingFilter === 'liked' ? '#16AA40' : '#272727'} />
            </NavIconWrapper>
            <Text size='xs' weight='normal' textcolor={floatingFilter === 'liked' ? '#16AA40' : '#272727'}>אהבתי</Text>
          </NavItem>

          <NavItem onClick={() => onFilterChange('benefits')}>
            <NavIconWrapper>
              <CouponIcon fill={floatingFilter === 'benefits' ? '#16AA40' : '#272727'} />
            </NavIconWrapper>
            <Text size='xs' weight='normal' textcolor={floatingFilter === 'benefits' ? '#16AA40' : '#272727'}>הטבות</Text>
          </NavItem>
        </>
      )}
    </BottomNav>
  );
};

export default BottomNavMobile;
